import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import type { UseCollectionResult } from '@hooks/dashboard/useCollection'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { FINANCE_GROSS_NET_BAR_COLORS } from '@constants/Dashboard/financeDashboard.constants'
import {
  financeTrendLine,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  FinanceMetricTilesRow,
  FinanceCollectionTrendHeader,
} from '@components/pages/Dashboard/Finance/FinanceMetricTiles.component'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'

type CollectionCardProps = {
  titleHref: string
  range: RangeKey
  collection: UseCollectionResult
}

export function CollectionCard({
  titleHref,
  range,
  collection,
}: CollectionCardProps): React.ReactElement {
  const {
    loading,
    grossStr,
    netStr,
    rateStr,
    changeSignedPct,
    changeAmt,
    grossNetChart,
    collGoalLabel,
    collGoalLine,
    hasPayload,
    hasGrossData,
    hasNetData,
    hasRateData,
    hasTrendData,
  } = collection
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasGrossData || hasNetData || hasRateData || hasTrendData,
  )
  const isChartEmpty = shouldShowDashboardSectionEmpty(loading, hasTrendData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  const placeholder = HYGIENE_PLACEHOLDER
  const trendLine = loading
    ? undefined
    : financeTrendLine(range, changeSignedPct, changeAmt)

  return (
    <DashboardSectionCard
      title="Collection"
      titleHref={titleHref}
      className="dashboard-finance-collection-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      headerRight={
        isEmpty ? undefined : (
          <FinanceCollectionTrendHeader trendLine={trendLine} />
        )
      }
    >
      <FinanceMetricTilesRow
        columns={4}
        metrics={[
          {
            id: 'gross-coll',
            leftLabel: 'Gross collection',
            value: loading ? placeholder : grossStr,
          },
          {
            id: 'net-coll',
            leftLabel: 'Net collection',
            value: loading ? placeholder : netStr,
          },
          {
            id: 'goal-coll',
            leftLabel: 'Goal',
            value: loading ? placeholder : collGoalLabel,
          },
          {
            id: 'rate-coll',
            leftLabel: 'Collection rate',
            value: loading ? placeholder : rateStr,
          },
        ]}
      />
      {isChartEmpty ? (
        <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
      ) : (
        <div className="dashboard-finance-collection-chart-wrap">
          <div className="dashboard-finance-collection-chart">
            <ResponsiveContainer width="100%" height={302}>
              <LineChart
                data={loading ? [] : grossNetChart.data}
                margin={{ top: 12, right: 8, left: 4, bottom: 12 }}
              >
                <CartesianGrid
                  strokeDasharray="0"
                  vertical={false}
                  horizontal
                  stroke="var(--gray-200)"
                />
                <XAxis
                  type="category"
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 14,
                    fill: 'var(--gray-400)',
                    fontWeight: 500,
                  }}
                  tickMargin={8}
                />
                <YAxis
                  type="number"
                  domain={grossNetChart.yDomain}
                  ticks={grossNetChart.yTicks}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 14,
                    fill: 'var(--gray-400)',
                    fontWeight: 500,
                  }}
                  tickFormatter={(v) =>
                    v === collGoalLine ? `$${v}K Goal` : `$${v}K`
                  }
                  width={56}
                />
                <ReferenceLine
                  y={collGoalLine}
                  stroke="var(--blue-500)"
                  strokeDasharray="4 4"
                  strokeWidth={1}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `$${value}K`,
                    name,
                  ]}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid var(--gray-200)',
                  }}
                  labelFormatter={(label) => String(label)}
                />
                <Line
                  type="linear"
                  dataKey="Gross"
                  name="Gross collection"
                  stroke={FINANCE_GROSS_NET_BAR_COLORS.gross}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line
                  type="linear"
                  dataKey="Net"
                  name="Net collection"
                  stroke={FINANCE_GROSS_NET_BAR_COLORS.net}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="dashboard-finance-collection-chart-legend">
            <span className="dashboard-finance-collection-chart-legend-item">
              <span
                className="dashboard-finance-collection-chart-legend-dot"
                style={{
                  background: FINANCE_GROSS_NET_BAR_COLORS.gross,
                }}
              />
              Gross collection
            </span>
            <span className="dashboard-finance-collection-chart-legend-item">
              <span
                className="dashboard-finance-collection-chart-legend-dot"
                style={{
                  background: FINANCE_GROSS_NET_BAR_COLORS.net,
                }}
              />
              Net collection
            </span>
          </div>
        </div>
      )}
    </DashboardSectionCard>
  )
}
